/** @jsx jsx */
import React, { useContext, useState, useCallback } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { useTransition, animated as a, config } from "react-spring"
import { Input, Button, Text, Spinner, jsx } from "theme-ui"
import BlockContent from "@sanity/block-content-to-react"
import addToMailchimp from "gatsby-plugin-mailchimp"

import { Context } from "../../context/MainContext"

import useEventListener from "../../hooks/useEventListener"

import { fbTrack } from "../../util"
import enums from "../../util/enums"

const styles = {
  container: {
    overflow: "hidden",
    fontSize: ["24px", "32px"],
    position: "fixed",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "secondary",
    zIndex: 100,
    margin: "0px",
    padding: "0px",
    top: "0px",
    left: "0px",
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    maxHeight: "-webkit-fill-available",
    color: "#333333",
  },
  inner: {
    width: "100%",
    position: "absolute",
    maxWidth: ["90%", "90%", "450px"],
    padding: "15px 20px 40px",
    backgroundColor: "#F7E8A1",
    border: "1px solid #333333",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "height 250ms linear",
  },
  form: {
    input: {
      width: "100%",
      borderRadius: "0px",
      margin: "0px 0px 16px",
      textAlign: "center",
    },
    button: {
      fontSize: "24px",
      width: "100%",
      border: "none",
      backgroundColor: "#333333",
      color: "#F7E8A1",
      borderRadius: "0px",
    },
  },
  closeButton: {
    fontSize: "18px",
    padding: "3px",
    backgroundColor: "transparent",
    color: "#333333",
  },
  smallText: {
    marginTop: "0.5em",
    a: {
      textDecoration: "underline",
      color: "#333",
    },
    fontSize: "12px",
  },
}

function EmailModal() {
  const [modalMode, setModalMode] = useState(enums.modalMode.default)
  const [emailValue, setEmailValue] = useState("")
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")

  const data = useStaticQuery(graphql`
    query EmailModalQuery {
      sanityNewsletterModal(_id: { eq: "newsletterModal" }) {
        cta
        discountCode
        successCopy: _rawSuccessCopy
        unsubscribeCopy: _rawUnsubscribeCopy
      }
    }
  `)

  const {
    cta,
    discountCode,
    successCopy,
    unsubscribeCopy,
  } = data.sanityNewsletterModal

  const BlockRenderer = props => {
    if (props.node.style === "normal") {
      return (
        <Text as="p" sx={styles.smallText}>
          {props.children}
        </Text>
      )
    }
    return BlockContent.defaultSerializers.types.block(props)
  }

  const {
    store: { isEmailModalOpen },
    toggleEmailModal,
    setEmailTimestamp,
  } = useContext(Context)

  function handleSetMessage(message) {
    setMessage(message)
    const timeout = setTimeout(() => {
      setMessage("")
      clearTimeout(timeout)
    }, 8000)
  }

  function handleEmailSubmission(value) {
    setLoading(true)

    addToMailchimp(value)
      .then(data => {
        if (data.result === "error") {
          handleSetMessage(data.msg)
        } else {
          setModalMode(enums.modalMode.success)
          setEmailTimestamp()
          fbTrack("track", "Complete Registration")
        }
      })
      .finally(() => {
        setLoading(false)
      })
      .catch(e => console.error(e))
  }

  const handleClose = useCallback(() => {
    setEmailTimestamp()
    toggleEmailModal()
  }, [setEmailTimestamp, toggleEmailModal])

  const closeHandler = useCallback(
    e => {
      if (e.key === "Escape" && isEmailModalOpen) handleClose()
    },
    [handleClose, isEmailModalOpen]
  )

  useEventListener("keydown", closeHandler)

  const copy = {
    [enums.modalMode.default]: {
      key: "default",
      content: (
        <React.Fragment>
          <Button sx={styles.closeButton} onClick={() => handleClose()}>
            {"[ X ]"}
          </Button>
          <Text as="p">{cta}</Text>
          <form
            sx={styles.form}
            onSubmit={e => {
              e.preventDefault()
              handleEmailSubmission(emailValue)
            }}
          >
            <Input
              placeholder="EMAIL"
              value={emailValue}
              onChange={e => setEmailValue(e.target.value)}
              type="email"
            />
            <Button type="submit">
              {loading ? (
                <Spinner sx={{ color: "#F7E8A1" }} size={32} strokeWidth={3} />
              ) : (
                "Submit"
              )}
            </Button>
          </form>
          {message ? (
            <Text
              as="p"
              sx={styles.smallText}
              dangerouslySetInnerHTML={{ __html: message }}
            />
          ) : (
            <BlockContent
              blocks={unsubscribeCopy}
              serializers={{ types: { block: BlockRenderer } }}
            />
          )}
        </React.Fragment>
      ),
    },
    [enums.modalMode.success]: {
      key: "success",
      content: (
        <React.Fragment>
          <Button sx={styles.closeButton} onClick={() => handleClose()}>
            {"[ X ]"}
          </Button>
          <Text
            sx={{ margin: "0 auto", maxWidth: "80%", p: { margin: "1em 0" } }}
          >
            <BlockContent
              blocks={successCopy}
              serializers={{ types: { block: BlockRenderer } }}
            />
          </Text>
          <Text as="p" sx={{ textDecoration: "underline" }}>
            {discountCode}
          </Text>
        </React.Fragment>
      ),
    },
  }

  const transitions = useTransition(isEmailModalOpen, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    duration: 1000,
  })

  const copyTransitions = useTransition(copy[modalMode], item => item.key, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.stiff,
  })

  return transitions.map(
    ({ item, key, props }) =>
      item && (
        <a.div key={key} style={props} tabIndex="0" sx={styles.container}>
          {copyTransitions.map(
            ({ item, key, props }) =>
              item && (
                <a.div style={props} key={key} sx={styles.inner}>
                  {item.content}
                </a.div>
              )
          )}
        </a.div>
      )
  )
}

export default EmailModal
